import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, Filter, Check } from 'lucide-react';
import { createSearch } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

export const AddSearch: React.FC = () => {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [sellerType, setSellerType] = useState('ANY');
  const [includeKeywords, setIncludeKeywords] = useState('');
  const [excludeKeywords, setExcludeKeywords] = useState('');
  const [hideFrequentSellers, setHideFrequentSellers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const plan = user?.active_subscription?.plan;
  const canSellerFilter = !!plan?.seller_filter;
  const canAdvancedFilters = !!plan?.advanced_filters;

  const extractName = (url: string) => {
    try {
      const urlObj = new URL(url);
      const q = urlObj.searchParams.get('q');
      if (q) return q.replace(/\+/g, ' ').replace(/%20/g, ' ');
      const parts = urlObj.pathname.split('/').filter(Boolean);
      const cleaned = parts.map((s) => s.replace(/-AS[A-Za-z0-9_~%]{6,}$/, '')).filter(Boolean);
      if (cleaned.length > 0) {
        let name = cleaned[cleaned.length - 1];
        if (cleaned.length >= 3 && (name.length <= 8 || /^\d+[_ ]?(gb|tb|гб|тб)$/i.test(name))) {
          name = cleaned[cleaned.length - 2] + ' ' + name;
        }
        return name.replace(/[_-]/g, ' ');
      }
    } catch {}
    return 'Новый поиск';
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
    if (!name && value) {
      setName(extractName(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;

    setLoading(true);
    try {
      const filters: any = {};
      if (priceMin) filters.price_min = parseInt(priceMin);
      if (priceMax) filters.price_max = parseInt(priceMax);
      if (sellerType !== 'ANY') filters.seller_type = sellerType;
      if (includeKeywords) filters.include_keywords = includeKeywords.split(',').map(s => s.trim()).filter(Boolean);
      if (excludeKeywords) filters.exclude_keywords = excludeKeywords.split(',').map(s => s.trim()).filter(Boolean);
      if (hideFrequentSellers) filters.hide_frequent_sellers = true;

      await createSearch({
        name: name || extractName(url),
        url: url.trim(),
        source: 'avito',
        filters: Object.keys(filters).length > 0 ? filters : undefined,
      });

      navigate('/searches');
    } catch (e: any) {
      alert('Ошибка: ' + (e.response?.data?.detail || 'Не удалось создать поиск'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>🔍 Добавить поиск</h1>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.field}>
          <label style={styles.label}>
            <Link size={14} /> Ссылка на Avito
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://www.avito.ru/..."
            style={styles.input}
            required
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Название поиска</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например: DJI Pocket 3"
            style={styles.input}
          />
        </div>

        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          style={styles.filterToggle}
        >
          <Filter size={16} />
          <span>Фильтры {showFilters ? '(скрыть)' : '(показать)'}</span>
        </button>

        {showFilters && (
          <div style={styles.filters}>
            <div style={styles.row}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Цена от</label>
                <input
                  type="number"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  placeholder="0"
                  style={styles.input}
                />
              </div>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Цена до</label>
                <input
                  type="number"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  placeholder="∞"
                  style={styles.input}
                />
              </div>
            </div>

            {canSellerFilter ? (
              <>
                <div style={styles.field}>
                  <label style={styles.label}>Тип продавца</label>
                  <select
                    value={sellerType}
                    onChange={(e) => setSellerType(e.target.value)}
                    style={styles.select}
                  >
                    <option value="ANY">Любой</option>
                    <option value="PRIVATE">Только частные</option>
                    <option value="BUSINESS">Только магазины</option>
                  </select>
                </div>

                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={hideFrequentSellers}
                    onChange={(e) => setHideFrequentSellers(e.target.checked)}
                  />
                  <span>Скрывать частых продавцов (вероятно, магазины)</span>
                </label>

              </>
            ) : (
              <div style={styles.upsellHint}>
                Фильтр продавцов и скрытие продавцов доступны на тарифах PRO и EXPERT
              </div>
            )}

            {canAdvancedFilters ? (
              <>
                <div style={styles.field}>
                  <label style={styles.label}>Включить слова (через запятую)</label>
                  <input
                    type="text"
                    value={includeKeywords}
                    onChange={(e) => setIncludeKeywords(e.target.value)}
                    placeholder="creator combo, pocket 3"
                    style={styles.input}
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Исключить слова (через запятую)</label>
                  <input
                    type="text"
                    value={excludeKeywords}
                    onChange={(e) => setExcludeKeywords(e.target.value)}
                    placeholder="ремонт, разбит, запчасти"
                    style={styles.input}
                  />
                </div>
              </>
            ) : (
              <div style={styles.upsellHint}>
                Расширенные фильтры (по ключевым словам) доступны на тарифе EXPERT
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !url.trim()}
          style={{
            ...styles.submit,
            opacity: loading || !url.trim() ? 0.6 : 1,
          }}
        >
          {loading ? (
            <span>Создание...</span>
          ) : (
            <>
              <Check size={18} />
              <span>Запустить мониторинг</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    margin: '0 0 20px 0',
    color: 'var(--tg-theme-text-color, #000)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--tg-theme-hint-color, #666)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  input: {
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid var(--tg-theme-hint-color, #e0e0e0)',
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #fff)',
    color: 'var(--tg-theme-text-color, #000)',
    fontSize: '15px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid var(--tg-theme-hint-color, #e0e0e0)',
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #fff)',
    color: 'var(--tg-theme-text-color, #000)',
    fontSize: '15px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  filterToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 0',
    background: 'none',
    border: 'none',
    color: 'var(--tg-theme-link-color, #2481cc)',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  filters: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '14px',
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #fff)',
    borderRadius: '16px',
  },
  row: {
    display: 'flex',
    gap: '12px',
  },
  submit: {
    marginTop: '8px',
    padding: '14px',
    backgroundColor: 'var(--tg-theme-button-color, #2481cc)',
    color: 'var(--tg-theme-button-text-color, #fff)',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: 'var(--tg-theme-text-color, #000)',
    padding: '4px 0',
    cursor: 'pointer',
  },
  upsellHint: {
    fontSize: '13px',
    color: 'var(--tg-theme-hint-color, #666)',
    padding: '10px 12px',
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #f5f5f5)',
    borderRadius: '10px',
  },
};
